import React, { useState, useEffect } from 'react';
import { Button, Input } from '@/components/ui';
import ComboBox from '@/components/ui/ComboBox';
import axios from 'axios';
import { LeadType } from '@/types';
import type { Division, User } from '@/types';

interface DivisionFormProps {
  initialData?: Division | null;
  onSubmit: (data: Partial<Division>) => void;
  onBudgetSubmit?: (data: { annualBudget?: number; budgetFiscalYear?: number }) => void;
  onCancel: () => void;
  isLoading?: boolean;
  canEditBudget?: boolean;
}

const DivisionForm: React.FC<DivisionFormProps> = ({
  initialData,
  onSubmit,
  onBudgetSubmit,
  onCancel,
  isLoading = false,
  canEditBudget = false,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [manualEntries, setManualEntries] = useState<string[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [formData, setFormData] = useState<{
    name: string;
    leadId: string;
    leadText: string;
    leadType: LeadType;
    budgetCode: string;
    annualBudget: string;
    budgetFiscalYear: string;
  }>({
    name: '',
    leadId: '',
    leadText: '',
    leadType: LeadType.USER,
    budgetCode: '',
    annualBudget: '',
    budgetFiscalYear: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch users and manual entries with DIVISION_LEAD_SCOPE on component mount
  useEffect(() => {
    const fetchDivisionLeads = async () => {
      setUsersLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get('/api/v1/user-scopes/suggestions?scope=DIVISION_LEAD_SCOPE', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          const suggestions = response.data.data.suggestions || [];

          // Extract USER type suggestions and convert to User format
          const userSuggestions = suggestions
            .filter((s: any) => s.type === 'USER')
            .map((s: any) => ({
              id: s.id,
              firstName: s.label.split(' ')[0],
              lastName: s.label.split(' ').slice(1).join(' '),
              email: s.email,
            }));

          // Extract manual TEXT entries
          const manualSuggestions = suggestions
            .filter((s: any) => s.type === 'TEXT')
            .map((s: any) => s.label);

          setUsers(userSuggestions);
          setManualEntries(manualSuggestions);
        }
      } catch (error) {
        console.error('Failed to fetch division leads:', error);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchDivisionLeads();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        leadId: initialData.leadId || '',
        leadText: initialData.leadText || '',
        leadType: initialData.leadType || LeadType.USER,
        budgetCode: initialData.budgetCode || '',
        annualBudget: initialData.annualBudget != null ? String(initialData.annualBudget) : '',
        budgetFiscalYear: initialData.budgetFiscalYear != null ? String(initialData.budgetFiscalYear) : '',
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Division name is required';
    } else if (formData.name.length < 1) {
      newErrors.name = 'Division name must be at least 1 character';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Division name must not exceed 100 characters';
    }

    // Lead is required (either leadId or leadText based on type)
    if (formData.leadType === LeadType.USER && !formData.leadId) {
      newErrors.leadId = 'Division lead is required';
    } else if (formData.leadType === LeadType.TEXT && !formData.leadText.trim()) {
      newErrors.leadText = 'Division lead is required';
    }

    // Validate budget fields if provided
    if (formData.annualBudget) {
      const budget = parseFloat(formData.annualBudget);
      if (isNaN(budget) || budget < 0) {
        newErrors.annualBudget = 'Annual budget must be a positive number';
      } else if (budget > 999999999999999) {
        newErrors.annualBudget = 'Annual budget exceeds maximum value';
      }
    }

    if (formData.budgetFiscalYear) {
      const year = parseInt(formData.budgetFiscalYear, 10);
      if (isNaN(year) || year < 2020 || year > 2050) {
        newErrors.budgetFiscalYear = 'Fiscal year must be between 2020 and 2050';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      name: formData.name,
      leadType: formData.leadType,
      budgetCode: formData.budgetCode || undefined,
      ...(formData.leadType === LeadType.USER
        ? { leadId: formData.leadId || undefined, leadText: undefined }
        : { leadId: undefined, leadText: formData.leadText || undefined }),
    };

    onSubmit(submitData);

    // Submit budget separately if canEditBudget and there are budget changes
    if (canEditBudget && onBudgetSubmit && initialData) {
      const hasBudgetChanges =
        (formData.annualBudget !== '' && String(initialData.annualBudget ?? '') !== formData.annualBudget) ||
        (formData.budgetFiscalYear !== '' && String(initialData.budgetFiscalYear ?? '') !== formData.budgetFiscalYear);

      if (hasBudgetChanges) {
        const budgetData: { annualBudget?: number; budgetFiscalYear?: number } = {};
        if (formData.annualBudget) {
          budgetData.annualBudget = parseFloat(formData.annualBudget);
        }
        if (formData.budgetFiscalYear) {
          budgetData.budgetFiscalYear = parseInt(formData.budgetFiscalYear, 10);
        }
        if (Object.keys(budgetData).length > 0) {
          onBudgetSubmit(budgetData);
        }
      }
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleLeadChange = (value: string, type: 'USER' | 'TEXT') => {
    setFormData((prev) => ({
      ...prev,
      leadType: type as LeadType,
      leadId: type === 'USER' ? value : '',
      leadText: type === 'TEXT' ? value : '',
    }));

    // Clear lead-related errors
    if (errors.leadId || errors.leadText) {
      setErrors((prev) => ({
        ...prev,
        leadId: '',
        leadText: '',
      }));
    }
  };

  // Prepare user options for ComboBox - includes both users and manual entries
  const userOptions = [
    // User suggestions
    ...users.map((user) => ({
      id: user.id,
      label: `${user.firstName} ${user.lastName}`,
      email: user.email,
    })),
    // Manual entry suggestions with indicator
    ...manualEntries.map((entry) => ({
      id: entry,
      label: entry,
      email: '(Previously used)',
    })),
  ];

  // Get current lead value for ComboBox
  const getCurrentLeadValue = () => {
    if (formData.leadType === LeadType.USER && formData.leadId) {
      return formData.leadId;
    } else if (formData.leadType === LeadType.TEXT && formData.leadText) {
      return formData.leadText;
    }
    return '';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 gap-3">
        <Input
          label="Division Name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          error={errors.name}
          placeholder="e.g., Technology Division"
          required
          fullWidth
        />

        <div className="space-y-1.5">
          <ComboBox
            label="Division Lead"
            options={userOptions}
            value={getCurrentLeadValue()}
            onChange={handleLeadChange}
            placeholder="Select a user or enter manually..."
            allowFreeText={true}
            disabled={usersLoading}
            error={errors.leadId || errors.leadText}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            The person responsible for this division. You can select from existing users or enter
            manually.
          </p>
        </div>

        <Input
          label="Budget Code"
          value={formData.budgetCode}
          onChange={(e) => handleInputChange('budgetCode', e.target.value)}
          error={errors.budgetCode}
          placeholder="e.g., DIV-001 (optional)"
          fullWidth
        />

        {/* Budget Fields - Only shown to FINANCE/ADMIN when editing */}
        {canEditBudget && initialData && (
          <>
            <div className="pt-3 border-t border-border">
              <p className="text-sm font-medium text-foreground mb-3">Budget Information</p>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Annual Budget ($)"
                  type="number"
                  value={formData.annualBudget}
                  onChange={(e) => handleInputChange('annualBudget', e.target.value)}
                  error={errors.annualBudget}
                  placeholder="e.g., 50000000"
                  fullWidth
                  min={0}
                  step={1000}
                />
                <Input
                  label="Fiscal Year"
                  type="number"
                  value={formData.budgetFiscalYear}
                  onChange={(e) => handleInputChange('budgetFiscalYear', e.target.value)}
                  error={errors.budgetFiscalYear}
                  placeholder="e.g., 2026"
                  fullWidth
                  min={2020}
                  max={2050}
                />
              </div>
              <p className="text-xs text-muted-foreground/60 mt-2">
                Budget updates require FINANCE or ADMIN role.
              </p>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading} disabled={isLoading}>
          {initialData ? 'Update' : 'Create'} Division
        </Button>
      </div>
    </form>
  );
};

export default DivisionForm;
